import React from 'react';
import { Link } from 'react-router-dom';
import { isAuthenticated, getUserType } from '../api/api';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import './Footer.css';

function Footer() {
  const loggedIn = isAuthenticated();
  const userType = getUserType();
  const showFindCreators = MILAN_WORKSPACE_ENABLED && loggedIn && userType === 'brand';
  // Performance Hub is a brand-only product. Show it to anonymous visitors
  // (marketing discovery) and to brands, but hide it from logged-in creators
  // so they don't land on a page that just bounces them back to their dashboard.
  const showPerformanceHub = !(loggedIn && userType === 'creator');

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-main">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img src="/logo.png" alt="Milan AI" className="footer-logo-image" />
              <span className="logo-text">Milan AI</span>
            </Link>
            <p className="footer-tagline">
              {MILAN_WORKSPACE_ENABLED
                ? 'Connecting brands with the perfect creators for impactful collaborations.'
                : 'Instagram performance tracking for brands and agencies — upload your roster, measure what works.'}
            </p>
            <div className="social-links">
              <a
                href="https://www.linkedin.com/company/influencers-milan-ai/"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Milan AI on LinkedIn"
                title="LinkedIn"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/influencers_milan.ai/"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Milan AI on Instagram"
                title="Instagram"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.39A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.73 1.46 1.39 2.13a5.88 5.88 0 0 0 2.13 1.39c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.39 5.88 5.88 0 0 0 1.39-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.39-2.13A5.88 5.88 0 0 0 19.86.63C19.1.33 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" />
                </svg>
              </a>
            </div>
          </div>
          
          <div className="footer-links">
            <div className="footer-column">
              <h4>Products</h4>
              <ul>
                {showFindCreators && (
                  <li><Link to="/find-creators">Campaigns &amp; Find Creators</Link></li>
                )}
                {showPerformanceHub && (
                  <li>
                    <Link to="/performance">
                      Performance Hub
                      <span className="footer-live-pill" aria-label="Live feature">LIVE</span>
                    </Link>
                  </li>
                )}
                <li><Link to="/services">All Services</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4>Resources</h4>
              <ul>
                <li><Link to="/resources">Free Tools</Link></li>
                <li><Link to="/resources">Engagement Calculator</Link></li>
                <li><Link to="/resources">Pricing Calculator</Link></li>
                <li><Link to="/resources">Top Influencers</Link></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4>Company</h4>
              <ul>
                <li><Link to="/">About Us</Link></li>
                <li><Link to="/">Careers</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/">Press</Link></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/data-deletion">Data Deletion</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Milan AI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
